// src/components/EditableDataDisplay.js
import React, { useState, useEffect } from 'react';
import { parseValueWithConfidence } from '../utils/confidenceUtils';
import ConfidenceIndicator from './confidenceIndicator';
import { Save, X } from 'lucide-react';
import favicon from "../assets/favicon_new.png";
import { motion } from 'framer-motion';


const EditableDataDisplay = ({ 
  data, 
  themeClasses, 
  isDarkMode, 
  isEditing, 
  isSaving = false,
  onSave, 
  onCancel,
  onEdit 
}) => {
  const [editData, setEditData] = useState(null);

  // Initialize edit data when editing starts
  useEffect(() => {
    if (isEditing && !editData) {
      // Handle null or undefined data
      if (!data && data !== 0 && data !== false) {
        setEditData({});
        return;
      }

      let parsedData = data;
      if (typeof data === 'string') {
        try {
          let cleanedData = data.trim();
          if (cleanedData.startsWith('```')) {
            const match = cleanedData.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
            if (match) {
              cleanedData = match[1];
            }
          }
          parsedData = JSON.parse(cleanedData);
        } catch (e) {
          parsedData = data;
        }
      }
      
      // Ensure we have a valid object to edit
      if (parsedData && typeof parsedData === 'object') {
        setEditData(JSON.parse(JSON.stringify(parsedData))); // Deep copy
      } else {
        setEditData({});
      }
    }
  }, [isEditing, data, editData]);

  const updateValue = (path, newValue) => {
    if (!editData) return;
    
    const newData = JSON.parse(JSON.stringify(editData));
    let current = newData;
    
    for (let i = 0; i < path.length - 1; i++) {
      if (!current[path[i]]) {
        current[path[i]] = {};
      }
      current = current[path[i]];
    }
    
    // Try to parse numbers
    if (!isNaN(newValue) && newValue !== '') {
      current[path[path.length - 1]] = Number(newValue);
    } else if (newValue === 'true' || newValue === 'false') {
      current[path[path.length - 1]] = newValue === 'true';
    } else {
      current[path[path.length - 1]] = newValue;
    }
    
    setEditData(newData);
  };

  const addArrayItem = (path) => {
    if (!editData) return;
    
    const newData = JSON.parse(JSON.stringify(editData));
    let current = newData;
    
    for (let i = 0; i < path.length; i++) {
      if (!current[path[i]]) {
        current[path[i]] = [];
      }
      current = current[path[i]];
    }
    
    if (Array.isArray(current)) {
      // Add a new item based on the structure of existing items
      if (current.length > 0 && typeof current[0] === 'object') {
        const template = {};
        Object.keys(current[0]).forEach(key => {
          template[key] = '';
        });
        current.push(template);
      } else {
        current.push('');
      }
    }
    
    setEditData(newData);
  };

  const removeArrayItem = (path, index) => {
    if (!editData) return;
    
    const newData = JSON.parse(JSON.stringify(editData));
    let current = newData;
    
    for (let i = 0; i < path.length; i++) {
      if (!current[path[i]]) return; // Exit if path doesn't exist
      current = current[path[i]];
    }
    
    if (Array.isArray(current)) {
      current.splice(index, 1);
    }
    
    setEditData(newData);
  };

  const renderEditableValue = (value, path = []) => {
    // Handle null or undefined values
    if (value === null || value === undefined) {
      return (
        <input
          type="text"
          value=""
          onChange={(e) => updateValue(path, e.target.value)}
          className={`w-full px-2 py-1 rounded border text-sm ${
            isDarkMode 
              ? 'bg-gray-700 border-gray-600 text-gray-200' 
              : 'bg-white border-gray-300 text-gray-900'
          } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
          placeholder="Enter value"
        />
      );
    }
    
    if (typeof value === 'string' || typeof value === 'number') {
      const { text } = parseValueWithConfidence(String(value));
      return (
        <input
          type="text"
          value={text}
          onChange={(e) => updateValue(path, e.target.value)}
          className={`w-full px-2 py-1 rounded border text-sm ${
            isDarkMode 
              ? 'bg-gray-700 border-gray-600 text-gray-200' 
              : 'bg-white border-gray-300 text-gray-900'
          } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
        />
      );
    } else if (typeof value === 'boolean') {
      return (
        <select
          value={String(value)}
          onChange={(e) => updateValue(path, e.target.value === 'true')}
          className={`px-2 py-1 rounded border text-sm ${
            isDarkMode 
              ? 'bg-gray-700 border-gray-600 text-gray-200' 
              : 'bg-white border-gray-300 text-gray-900'
          } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
        >
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      );
    } else if (Array.isArray(value)) {
      const shouldRenderAsTable = value.length > 0 && typeof value[0] === 'object' && value[0] !== null;

      if (shouldRenderAsTable) {
        const headers = value.length > 0 ? Object.keys(value[0]) : [];

        return (
          <div className={`overflow-x-auto mt-2 border rounded ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
            <div className="flex justify-between items-center p-2 border-b border-gray-300 dark:border-gray-600">
              <span className="text-sm font-medium">Table Data</span>
              <button
                onClick={() => addArrayItem(path)}
                className={`px-2 py-1 text-xs rounded ${
                  isDarkMode 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                + Add Row
              </button>
            </div>
            <table className="min-w-full text-sm">
              <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <tr>
                  {headers.map(header => (
                    <th key={header} className={`px-3 py-1.5 text-left font-medium ${themeClasses.text} capitalize`}>
                      {header.replace(/_/g, ' ')}
                    </th>
                  ))}
                  <th className={`px-3 py-1.5 text-left font-medium ${themeClasses.text}`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`${isDarkMode ? 'divide-y divide-gray-600' : 'divide-y divide-gray-200'}`}>
                {value.map((item, index) => (
                  <tr key={index} className={`${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100'}`}>
                    {headers.map(header => (
                      <td key={header} className={`px-3 py-1.5`}>
                        {renderEditableValue(item[header] || '', [...path, index, header])}
                      </td>
                    ))}
                    <td className="px-3 py-1.5">
                      <button
                        onClick={() => removeArrayItem(path, index)}
                        className={`px-2 py-1 text-xs rounded ${
                          isDarkMode 
                            ? 'bg-red-600 hover:bg-red-700 text-white' 
                            : 'bg-red-500 hover:bg-red-600 text-white'
                        }`}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }

      return (
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">List Items</span>
            <button
              onClick={() => addArrayItem(path)}
              className={`px-2 py-1 text-xs rounded ${
                isDarkMode 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              + Add Item
            </button>
          </div>
          {value.map((item, index) => (
            <div key={index} className="flex items-center space-x-2">
              <div className="flex-1">
                {renderEditableValue(item, [...path, index])}
              </div>
              <button
                onClick={() => removeArrayItem(path, index)}
                className={`px-2 py-1 text-xs rounded ${
                  isDarkMode 
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : 'bg-red-500 hover:bg-red-600 text-white'
                }`}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      );
    } else if (typeof value === 'object' && value !== null) {
      return (
        <div className="mt-1 pl-4 border-l border-gray-300 dark:border-gray-600 space-y-2">
          {Object.entries(value).map(([key, val]) => (
            <div key={key} className="flex flex-col sm:flex-row sm:items-start">
              <span className={`sm:w-1/3 font-medium mr-2 ${themeClasses.subtext} capitalize mb-1 sm:mb-0`}>
                {key.replace(/_/g, ' ')}:
              </span>
              <div className={`sm:w-2/3`}>
                {renderEditableValue(val, [...path, key])}
              </div>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderReadOnlyValue = (value) => {
    // Handle null or undefined values
    if (value === null || value === undefined) {
      return <span className="text-gray-500 italic">No value</span>;
    }
    
    if (typeof value === 'string') {
      const { text, confidence } = parseValueWithConfidence(value);
      return (
        <span>
          {text}
          <ConfidenceIndicator confidence={confidence} />
        </span>
      );
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      return <span>{String(value)}</span>;
    } else if (Array.isArray(value)) {
      const shouldRenderAsTable = value.length > 0 && typeof value[0] === 'object' && value[0] !== null && !Array.isArray(value[0]);

      if (shouldRenderAsTable) {
        const headers = Object.keys(value[0]);

        return (
          <div className={`overflow-x-auto mt-2 border rounded ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
            <table className="min-w-full text-sm">
              <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <tr>
                  {headers.map(header => (
                    <th key={header} className={`px-3 py-1.5 text-left font-medium ${themeClasses.text} capitalize`}>
                      {header.replace(/_/g, ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={`${isDarkMode ? 'divide-y divide-gray-600' : 'divide-y divide-gray-200'}`}>
                {value.map((item, index) => (
                  <tr key={index} className={`${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100'}`}>
                    {headers.map(header => (
                      <td key={header} className={`px-3 py-1.5 ${themeClasses.text}`}>
                        {item.hasOwnProperty(header) ? renderReadOnlyValue(item[header]) : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }

      return (
        <ul className="list-disc pl-5 mt-1 space-y-1">
          {value.map((item, index) => (
            <li key={index} className={`${themeClasses.text}`}>{renderReadOnlyValue(item)}</li>
          ))}
        </ul>
      );
    } else if (typeof value === 'object' && value !== null) {
      return (
        <div className="mt-1 pl-4 border-l border-gray-300 dark:border-gray-600 space-y-1">
          {Object.entries(value).map(([key, val]) => (
            <div key={key} className="flex flex-col sm:flex-row">
              <span className={`sm:w-1/3 font-medium mr-2 ${themeClasses.subtext} capitalize`}>{key.replace(/_/g, ' ')}:</span>
              <div className={`sm:w-2/3 ${themeClasses.text}`}>{renderReadOnlyValue(val)}</div>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const handleSave = () => {
    if (onSave && editData) {
      onSave(editData);
    }
  };

  const handleCancel = () => {
    setEditData(null);
    if (onCancel) {
      onCancel();
    }
  };

  // Handle null or undefined data
  if (!data && data !== 0 && data !== false) {
    return <div className={`text-sm ${themeClasses.text} p-2 border rounded`}>No data available</div>;
  }

  let parsedData = data;
  if (typeof data === 'string') {
    try {
      let cleanedData = data.trim();
      if (cleanedData.startsWith('```')) {
        const match = cleanedData.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
        if (match) {
          cleanedData = match[1];
        }
      }
      parsedData = JSON.parse(cleanedData);
    } catch (e) {
      console.error("Failed to parse data string:", e);
      return <pre className={`whitespace-pre-wrap text-sm ${themeClasses.text} p-2 border rounded`}>{data}</pre>;
    }
  }

  if (typeof parsedData !== 'object' || parsedData === null) {
    return <pre className={`whitespace-pre-wrap text-sm ${themeClasses.text}`}>{String(data)}</pre>;
  }

  const dataToRender = isEditing ? editData : parsedData;

  // Additional safety check for the data to render
  if (!dataToRender || typeof dataToRender !== 'object') {
    return <div className={`text-sm ${themeClasses.text} p-2 border rounded`}>
      {isEditing ? 'Loading edit data...' : 'No valid data to display'}
    </div>;
  }

  const entries = Object.entries(dataToRender || {});
  const isFlatObject =
    !Array.isArray(dataToRender) &&
    entries.length > 0 &&
    entries.every(
      ([, value]) =>
        value === null ||
        value === undefined ||
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
    );

  return (
      <div className="space-y-2">
        {/* Edit Controls - Show only when editing */}
        {isEditing && (
          <div className="flex justify-end mb-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`flex items-center space-x-1 px-3 py-1.5 text-sm rounded transition-colors ${
                  isSaving 
                    ? (isDarkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-400 text-gray-200')
                    : (isDarkMode 
                        ? 'bg-green-600 hover:bg-green-700 text-white' 
                        : 'bg-green-500 hover:bg-green-600 text-white'
                      )
                }`}
              >
                {isSaving ? (
                  <>
                    <div className="relative w-3 h-3 mb-5">
  <motion.div
    className="w-full h-full"
    animate={{ rotate: 360 }}
    transition={{
      duration: 2,
      ease: "linear",
      repeat: Infinity,
    }}
  >
    <img 
      src={favicon} 
      alt="Loading" 
      className="w-full h-full object-contain" 
      onError={(e) => {
        console.log('Image failed to load:', e.target.src);
        e.target.style.display = 'none';
      }}
    />
  </motion.div>
</div>

                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save size={14} />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className={`flex items-center space-x-1 px-3 py-1.5 text-sm rounded transition-colors ${
                  isSaving 
                    ? (isDarkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-300 text-gray-400')
                    : (isDarkMode 
                        ? 'bg-gray-600 hover:bg-gray-700 text-white' 
                        : 'bg-gray-500 hover:bg-gray-600 text-white'
                      )
                }`}
              >
                <X size={14} />
                <span>Cancel</span>
              </button>
            </div>
          </div>
        )}

        {/* Data Display */}
        {isFlatObject ? (
          <div
            className={`overflow-x-auto border rounded ${
              isDarkMode ? 'border-gray-600' : 'border-gray-300'
            }`}
          >
            <table className="min-w-full text-sm">
              <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}>
                <tr>
                  <th
                    className={`px-3 py-2 text-left font-medium ${themeClasses.text} w-1/3`}
                  >
                    Field
                  </th>
                  <th
                    className={`px-3 py-2 text-left font-medium ${themeClasses.text}`}
                  >
                    Value
                  </th>
                </tr>
              </thead>
              <tbody
                className={
                  isDarkMode
                    ? 'divide-y divide-gray-600'
                    : 'divide-y divide-gray-200'
                }
              >
                {entries.map(([key, value]) => (
                  <tr
                    key={key}
                    className={
                      isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100'
                    }
                  >
                    <td
                      className={`px-3 py-2 font-medium capitalize align-top ${themeClasses.subtext}`}
                    >
                      {key.replace(/_/g, ' ')}
                    </td>
                    <td className={`px-3 py-2 ${themeClasses.text}`}>
                      {isEditing
                        ? renderEditableValue(value, [key])
                        : renderReadOnlyValue(value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          entries.map(([key, value]) => (
            <div key={key} className="flex flex-col sm:flex-row py-1">
              <span className={`sm:w-1/4 font-semibold mr-2 capitalize ${themeClasses.text}`}>
                {key.replace(/_/g, ' ')}:
              </span>
              <div className={`sm:w-3/4 ${themeClasses.text}`}>
                {isEditing ? renderEditableValue(value, [key]) : renderReadOnlyValue(value)}
              </div>
            </div>
          ))
        )}
      </div>
    );
};

export default EditableDataDisplay; 